/**
 * @file DiscardEditsDialogContainer.tsx
 * @description "Discard unsaved changes?" confirmation shown when a user
 *   clicks Cancel while in SAVED-extraction edit mode (SE-4347 AC-3).
 *   Stateless copy adapter over the shared ConfirmDialog
 *   molecule (SE-5524); the edit-mode hook
 *   (`useContractExtractionEditMode`) drives its open / close / confirm
 *   lifecycle. The `keep-editing-btn` / `confirm-discard-edits-btn`
 *   testIDs are pinned by the contracts e2e specs.
 * @remarks Distinct from `DiscardExtractionDialog` — this variant never
 *   mutates server state; it purely toggles local edit-mode back to view
 *   mode and signals the caller to refetch so the drawer re-renders the
 *   last-saved extraction data.
 * @module features/contracts/components/DiscardEditsDialog/DiscardEditsDialogContainer
 */

import { AlertTriangle } from "lucide-react-native";
import React, { memo } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Props for {@link DiscardEditsDialogContainer}. */
interface Props {
  /** Whether the dialog is currently visible. */
  readonly isOpen: boolean;
  /** Invoked when the user keeps editing (dismiss / backdrop / close). */
  readonly onKeepEditing: () => void;
  /** Invoked when the user confirms the discard. */
  readonly onConfirmDiscard: () => void;
}

/**
 * Renders the discard-unsaved-edits confirmation.
 * @param props - Component props
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onKeepEditing - Invoked when the user dismisses the dialog
 * @param props.onConfirmDiscard - Invoked when the user confirms discarding
 * @returns The rendered confirmation dialog.
 */
function DiscardEditsDialogContainer({
  isOpen,
  onKeepEditing,
  onConfirmDiscard,
}: Props) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onKeepEditing}
      onConfirm={onConfirmDiscard}
      title="Discard unsaved changes?"
      body="Your edits have not been saved. Discarding will revert this extraction to its last-saved state."
      icon={AlertTriangle}
      tone="danger"
      cancelLabel="Keep editing"
      confirmLabel="Discard"
      cancelTestID="keep-editing-btn"
      confirmTestID="confirm-discard-edits-btn"
    />
  );
}

DiscardEditsDialogContainer.displayName = "DiscardEditsDialogContainer";

export default memo(DiscardEditsDialogContainer);
